/* eslint-disable jsx-a11y/label-has-associated-control */
import React from 'react'
import Modal from 'src/components/Modal'
import * as S from './ModalSaveReceipts.style'
import {
  ReceivementInvoicesHtml, ReturnInvoice, SendJustificationReceiptsPayload, UploadReceiptsPayload,
} from 'src/features/receivement/ReceivementTypes'
import { Button } from '@mui/material'
import { useDispatch } from 'react-redux'
import { actions } from 'src/features/receivement/ReceivementState'
import { useForm } from 'react-hook-form'

interface ModalSaveReceiptsProps {
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
  isOpen: boolean
  currentInvoiceReturn?: ReturnInvoice[]
  currentReceivement: ReceivementInvoicesHtml | undefined
  setNextButtonIsDisabled: React.Dispatch<React.SetStateAction<boolean>>
}

export const ModalSaveReceipts = ({
  setIsOpen, isOpen, currentInvoiceReturn, currentReceivement, setNextButtonIsDisabled,
}: ModalSaveReceiptsProps) => {
  const [pdfFiles, setPdfFiles] = React.useState<{ invoiceKeyReturn: string, fileName: string, pdfFile: File }[]>([])
  const [nameSavedReceipts, setNameSavedReceipts] = React.useState({})
  const [isChecked, setIsChecked] = React.useState(false)
  const [justification, setJustification] = React.useState('' as string)

  const {
    setError, clearErrors, register, formState: { errors },
  } = useForm()

  const dispatch = useDispatch()

  const saveReceipts = React.useCallback((event, invoiceKeyReturn) => {
    const input = event.target
    const file = input.files[0]

    input.value = ''

    const newPdfFile = {
      invoiceKeyReturn,
      fileName: file.name,
      pdfFile: file,
    }

    setPdfFiles([...pdfFiles, newPdfFile])
    setNameSavedReceipts({
      ...nameSavedReceipts,
      [invoiceKeyReturn]: file.name,
    })
  }, [pdfFiles, nameSavedReceipts, setPdfFiles])

  const deletePdfFile = React.useCallback((invoiceKeyReturn, fileName) => {
    setPdfFiles(
      pdfFiles.filter(
        (pdfFile) => pdfFile.invoiceKeyReturn !== invoiceKeyReturn || pdfFile.pdfFile.name !== fileName,
      ),
    )
    setNameSavedReceipts({
      ...nameSavedReceipts,
      [invoiceKeyReturn]: undefined,
    })
  }, [pdfFiles, nameSavedReceipts, setPdfFiles])

  const onSubmit = () => {
    const payloadWithReceipts: UploadReceiptsPayload = { pdfFiles, idReceivement: currentReceivement?.idReceivement || 0, setNextButtonIsDisabled }
    const payloadWithoutReceipts: SendJustificationReceiptsPayload = { justification, idReceivement: currentReceivement?.idReceivement || 0, setNextButtonIsDisabled }
    if (isChecked) {
      if (!justification) {
        setError('justification', { type: 'required', message: 'Campo obrigatório' })
        return
      }
      dispatch(actions.sendJustificationReceipts(payloadWithoutReceipts))
    } else {
      dispatch(actions.uploadReceipts(payloadWithReceipts))
    }
    setIsOpen(false)
  }

  const getInvoiceReturn = () => {
    if (currentInvoiceReturn?.length) {
      return (
        <>
          <S.ContainerCheckBox>
            <>
              <input
                type='checkbox'
                checked={isChecked}
                onChange={() => setIsChecked(!isChecked)}
              />
              <p>O motorista não assinou os canhotos</p>
            </>
          </S.ContainerCheckBox>
          {isChecked && (
          <S.CustomTextFieldMui
            type='text'
            variant='outlined'
            name='justification'
            label='justification.receipts'
            id='justification'
            onChange={(event) => setJustification(event.target.value)}
            inputProps={{ ...register('justification', { required: 'Campo obrigatório' }), 'data-testid': 'justification-receipts-test' }}
            onInput={() => clearErrors('justification')}
            error={!!errors.justification}
            helperText={errors.justification?.message}
          />
          )}
          {currentInvoiceReturn.map((invoice, index) =>
            invoice && (
            <>
              <S.ListCustom key={index}>Chave da nota: {invoice.invoiceKeyReturn}
                <S.CustomContainerFile>
                  <S.CustomInput
                    type='file'
                    accept='application/pdf'
                    id='file-input'
                    data-testid='file-input-upload'
                    onChange={(event) => saveReceipts(event, invoice.invoiceKeyReturn)}
                    disabled={nameSavedReceipts[invoice.invoiceKeyReturn] !== undefined}
                  />
                  <S.CustomButton
                    variant='contained'
                    size='large'
                    type='button'
                    data-testid='button-upload-receipt'
                    disabled={nameSavedReceipts[invoice.invoiceKeyReturn] !== undefined}
                  >
                    Salvar canhoto
                  </S.CustomButton>
                </S.CustomContainerFile>
              </S.ListCustom>
              <S.ContainerObservation>
                {nameSavedReceipts[invoice.invoiceKeyReturn] && (
                <S.ContainerFile>
                  <S.CustomTextNameFile>Arquivo: {nameSavedReceipts[invoice.invoiceKeyReturn]}</S.CustomTextNameFile>
                  <Button type='button' onClick={() => deletePdfFile(invoice.invoiceKeyReturn, nameSavedReceipts[invoice.invoiceKeyReturn])}><S.DeleteIcon /> </Button>
                </S.ContainerFile>
                )}
                <S.CustomTextParagraph>*aceita apenas arquivos PDF</S.CustomTextParagraph>
              </S.ContainerObservation>
            </>
            ))}
        </>
      )
    }
    return 'Nenhuma nota de devolução retornada'
  }

  const verifyDisabled = !(isChecked || pdfFiles.length === currentInvoiceReturn?.length) || (isChecked && pdfFiles.length === currentInvoiceReturn?.length)

  return (
    <Modal
      testId='modal-save-receipt'
      show={isOpen}
      closedButton={false}
      title='Salvar canhotos'
      titleStyle={{ fontSize: '16px', letterSpacing: '1.5px' }}
      size='lg'
      IconLeft={() => <S.UploadIcon />}
      body={(
        <S.Container>
          <S.Title>Para cada nota de devolução, salve o canhoto respectivo</S.Title>
          {getInvoiceReturn()}
          <S.ConfirmButton
            variant='contained'
            size='large'
            type='button'
            onClick={onSubmit}
            data-testid='button-confirm-save-receipt'
            disabled={verifyDisabled}
          >
            Confirmar
          </S.ConfirmButton>
        </S.Container>
      )}
    />
  )
}
